import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import { sortCzech } from "@ckgo2/shared";
import { ArrowLeft, Plus, Save, Loader2, Users } from "lucide-react";
import toast from "react-hot-toast";
import {
  FIELD_REGISTRY,
  JOURNEY_SUBJECT_TYPES,
  JOURNEY_SEND_CLASSES,
  JOURNEY_REENROLLMENTS,
  type JourneySubjectType,
  type JourneyTrigger,
  type JourneyStep,
  type ConditionGroup,
  type JourneySendClass,
  type JourneyReenrollment,
} from "@ckgo2/shared";
import { SUBJECT_TYPE_LABELS, SEND_CLASS_LABELS, REENROLLMENT_LABELS } from "./labels";
import TriggerEditor from "./components/TriggerEditor";
import ConditionGroupEditor from "./components/ConditionGroupEditor";
import StepCard from "./components/StepCard";
import type { BuilderOptions } from "./components/ActionEditor";

interface JourneyDraft {
  name: string;
  description: string;
  enabled: boolean;
  subjectType: JourneySubjectType;
  trigger: JourneyTrigger;
  entryConditions: ConditionGroup | null;
  steps: JourneyStep[];
  reenrollment: JourneyReenrollment;
  maxConcurrentPerSubject: number;
  sendClass: JourneySendClass;
  priority: number;
}

const NEW_DRAFT: JourneyDraft = {
  name: "",
  description: "",
  enabled: false,
  subjectType: "order",
  trigger: { type: "status_change", to: [] },
  entryConditions: null,
  steps: [{ actions: [{ type: "send_email", recipient: "customer" }] }],
  reenrollment: "once",
  maxConcurrentPerSubject: 1,
  sendClass: "transactional",
  priority: 0,
};

const sel =
  "rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm";
const lbl = "block text-xs font-medium text-gray-500 mb-1";
const card = "rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4";

export default function JourneyEditPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === "new";

  const [draft, setDraft] = useState<JourneyDraft>(NEW_DRAFT);
  // Baseline = the draft last filled from the server (or saved by the editor). "Changed" = the
  // current draft is no longer that object: every edit calls `setDraft` with a new one. The
  // current draft is mirrored into a ref so the fill effect reads it without re-running on every
  // keystroke.
  const baselineRef = useRef<JourneyDraft | null>(null);
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);
  // The `id` the draft was last filled for; `undefined` until the first data, so the first data
  // for any `id` always fills.
  const lastFilledIdRef = useRef<string | undefined>(undefined);

  const {
    data: existing,
    dataUpdatedAt,
    isLoading,
  } = useQuery({
    queryKey: ["journey", id],
    queryFn: () => api.get<ApiResponse<JourneyDraft & { id: string }>>(`/api/admin/journeys/${id}`),
    enabled: !isNew,
  });

  // Fill the draft from the server. Data for another `id` always fills; for the same `id` (refetch
  // after a save) only when the editor has not changed the draft since, so edits typed after
  // "Uloženo" survive the refetch. `dataUpdatedAt` is a dependency because structural sharing
  // keeps the `existing` object when a refetch returns equal data, and that refetch must fill.
  useEffect(() => {
    if (existing?.data) {
      if (lastFilledIdRef.current === id && draftRef.current !== baselineRef.current) return;
      const d = existing.data;
      const filled: JourneyDraft = {
        name: d.name,
        description: d.description ?? "",
        enabled: d.enabled,
        subjectType: d.subjectType,
        trigger: d.trigger,
        entryConditions: d.entryConditions ?? null,
        steps: d.steps?.length ? d.steps : NEW_DRAFT.steps,
        reenrollment: d.reenrollment,
        maxConcurrentPerSubject: d.maxConcurrentPerSubject,
        sendClass: d.sendClass,
        priority: d.priority,
      };
      baselineRef.current = filled;
      lastFilledIdRef.current = id;
      setDraft(filled);
    }
  }, [id, existing, dataUpdatedAt]);

  // Option sources for action pickers.
  const usersQ = useQuery({
    queryKey: ["users"],
    queryFn: () =>
      api.get<ApiResponse<Array<{ id: string; name?: string; email?: string }>>>(
        "/api/admin/users",
      ),
  });
  const segmentsQ = useQuery({
    queryKey: ["segments"],
    queryFn: () => api.get<ApiResponse<Array<{ id: string; name: string }>>>("/api/admin/segments"),
  });
  const templatesQ = useQuery({
    queryKey: ["templates", "all"],
    queryFn: () =>
      api.get<ApiResponse<Array<{ slug: string; name: string; type: string }>>>(
        "/api/admin/templates",
      ),
  });

  const options: BuilderOptions = useMemo(
    () => ({
      // Lidé v pickerech akcí abecedně podle zobrazeného jména (`sortCzech`).
      users: sortCzech(
        (usersQ.data?.data ?? []).map((u) => ({ id: u.id, name: u.name || u.email || u.id })),
        (u) => u.name,
      ),
      segments: (segmentsQ.data?.data ?? []).map((s) => ({ id: s.id, name: s.name })),
      templates: (templatesQ.data?.data ?? [])
        .filter((tpl) => tpl.type === "email")
        .map((tpl) => ({ slug: tpl.slug, name: tpl.name })),
    }),
    [usersQ.data, segmentsQ.data, templatesQ.data],
  );

  const fields = FIELD_REGISTRY[draft.subjectType];

  const saveMutation = useMutation({
    mutationFn: (payload: JourneyDraft) =>
      isNew
        ? api.post<ApiResponse<{ id: string }>>("/api/admin/journeys", payload)
        : api.put<ApiResponse<{ id: string }>>(`/api/admin/journeys/${id}`, payload),
    // After save the editor stays put (vize F9.2); only a new journey moves to its detail URL.
    // Async on purpose: `isPending` (the disabled Save button) lasts until a new journey's detail
    // is loaded, so a second click cannot POST a duplicate.
    onSuccess: async (response, variables) => {
      toast.success(t("marketing.journeyEdit.saved"));
      queryClient.invalidateQueries({ queryKey: ["journeys"] });
      if (isNew) {
        const newId = response.data?.id;
        if (!newId) return;
        // Seed the detail cache with the GET shape before switching the URL (no spinner); the
        // POST response is never written into the detail cache.
        try {
          await queryClient.fetchQuery({
            queryKey: ["journey", newId],
            queryFn: () =>
              api.get<ApiResponse<JourneyDraft & { id: string }>>(`/api/admin/journeys/${newId}`),
            staleTime: 0,
          });
        } catch {
          // Saved either way; the detail then loads normally (with a spinner).
        }
        navigate(`/journeys/${newId}`, { replace: true });
        return;
      }
      // The saved draft becomes the baseline: unless the editor types on, the refetch replaces it
      // with what the server stored (e.g. `sendClass` normalised by role).
      baselineRef.current = variables;
      queryClient.invalidateQueries({ queryKey: ["journey", id] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : t("common.toasts.saveFailed")),
  });

  const save = () => {
    if (!draft.name.trim()) return toast.error(t("marketing.journeyEdit.nameRequired"));
    if (draft.steps.length === 0) return toast.error(t("marketing.journeyEdit.stepRequired"));
    if (draft.steps.some((s) => s.actions.length === 0))
      return toast.error(t("marketing.journeyEdit.actionRequired"));
    saveMutation.mutate(draft);
  };

  const setStep = (i: number, s: JourneyStep) =>
    setDraft((d) => ({ ...d, steps: d.steps.map((x, idx) => (idx === i ? s : x)) }));
  const moveStep = (i: number, dir: -1 | 1) =>
    setDraft((d) => {
      const next = [...d.steps];
      const j = i + dir;
      if (j < 0 || j >= next.length) return d;
      [next[i], next[j]] = [next[j], next[i]];
      return { ...d, steps: next };
    });

  if (!isNew && isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/journeys"
            className="rounded p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {isNew
              ? t("marketing.journeyEdit.newTitle")
              : draft.name || t("marketing.journeyEdit.editTitle")}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Link
              to={`/journeys/${id}/enrollments`}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              <Users className="h-4 w-4" /> {t("marketing.journeyEnrollments.title")}
            </Link>
          )}
          <button
            onClick={save}
            disabled={saveMutation.isPending}
            className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {t("common.actions.save")}
          </button>
        </div>
      </div>

      {/* Basics */}
      <div className={card + " space-y-3"}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className={lbl}>{t("common.fields.name")}</label>
            <input
              className={sel + " w-full"}
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </div>
          <div>
            <label className={lbl}>{t("marketing.journeyEdit.subjectType")}</label>
            <select
              className={sel + " w-full"}
              value={draft.subjectType}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  subjectType: e.target.value as JourneySubjectType,
                  trigger: { type: "status_change", to: [] },
                  entryConditions: null,
                })
              }
            >
              {JOURNEY_SUBJECT_TYPES.map((s) => (
                <option key={s} value={s}>
                  {SUBJECT_TYPE_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className={lbl}>{t("common.fields.description")}</label>
          <input
            className={sel + " w-full"}
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
          />
          {t("marketing.journeyEdit.enabled")}
        </label>
      </div>

      {/* Trigger */}
      <div className={card}>
        <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
          {t("marketing.journeyEdit.triggerTitle")}
        </h3>
        <TriggerEditor
          value={draft.trigger}
          onChange={(trigger) => setDraft({ ...draft, trigger })}
          fields={fields}
        />
      </div>

      {/* Entry conditions */}
      <div className={card}>
        <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
          {t("marketing.journeyEdit.entryConditionsTitle")}
        </h3>
        <ConditionGroupEditor
          value={draft.entryConditions}
          onChange={(entryConditions) => setDraft({ ...draft, entryConditions })}
          fields={fields}
        />
      </div>

      {/* Steps */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {t("marketing.journeyEdit.stepsTitle")}
        </h3>
        {draft.steps.map((s, i) => (
          <StepCard
            key={i}
            index={i}
            total={draft.steps.length}
            step={s}
            onChange={(ns) => setStep(i, ns)}
            onRemove={() =>
              setDraft((d) => ({ ...d, steps: d.steps.filter((_, idx) => idx !== i) }))
            }
            onMove={(dir) => moveStep(i, dir)}
            fields={fields}
            options={options}
          />
        ))}
        <button
          type="button"
          onClick={() =>
            setDraft((d) => ({
              ...d,
              steps: [...d.steps, { actions: [{ type: "send_email", recipient: "customer" }] }],
            }))
          }
          className="inline-flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          <Plus className="h-4 w-4" /> {t("marketing.journeyEdit.addStep")}
        </button>
      </div>

      {/* Policy */}
      <div className={card}>
        <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
          {t("marketing.journeyEdit.policyTitle")}
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <label className={lbl}>{t("marketing.journeyEdit.sendClass")}</label>
            <select
              className={sel + " w-full"}
              value={draft.sendClass}
              onChange={(e) =>
                setDraft({ ...draft, sendClass: e.target.value as JourneySendClass })
              }
            >
              {JOURNEY_SEND_CLASSES.map((s) => (
                <option key={s} value={s}>
                  {SEND_CLASS_LABELS[s]}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-400">
              {t("marketing.journeyEdit.sendClassHint")}
            </p>
          </div>
          <div>
            <label className={lbl}>{t("marketing.journeyEdit.reenrollment")}</label>
            <select
              className={sel + " w-full"}
              value={draft.reenrollment}
              onChange={(e) =>
                setDraft({ ...draft, reenrollment: e.target.value as JourneyReenrollment })
              }
            >
              {JOURNEY_REENROLLMENTS.map((r) => (
                <option key={r} value={r}>
                  {REENROLLMENT_LABELS[r]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={lbl}>{t("marketing.journeyEdit.maxConcurrent")}</label>
            <input
              type="number"
              min={1}
              className={sel + " w-full"}
              value={draft.maxConcurrentPerSubject}
              onChange={(e) =>
                setDraft({ ...draft, maxConcurrentPerSubject: Number(e.target.value) || 1 })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
